import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ServiceType } from '../../types/api';
import OptimizedImage from '../common/OptimizedImage';
import { imageAssets } from '../../assets/images';

interface BlacklistModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedItems: string[];
  onSelectionChange: (selectedItems: string[]) => void;
  serviceType?: ServiceType;
}

interface BlacklistItem {
  id: string;
  label: string;
  category?: string;
  image?: string | { png: string; webp: string; avif: string; };
}

// Export blacklist items so they can be used by other components
export const getFlowerBlacklistItems = (t: any): BlacklistItem[] => [
  { id: 'blacklist_roses', label: t('subscription.preferences.blacklistRoses', 'Roses'), category: 'types' },
  { id: 'blacklist_chrysanthemums', label: t('subscription.preferences.blacklistChrysanthemums', 'Chrysanthemums'), category: 'types' },
  { id: 'blacklist_lilies', label: t('subscription.preferences.blacklistLilies', 'Lilies'), category: 'types' },
  { id: 'blacklist_carnations', label: t('subscription.preferences.blacklistCarnations', 'Carnations'), category: 'types' },
  { id: 'blacklist_sunflowers', label: t('subscription.preferences.blacklistSunflowers', 'Sunflowers'), category: 'types' },
  { id: 'blacklist_tulips', label: t('subscription.preferences.blacklistTulips', 'Tulips'), category: 'types' },
  { id: 'blacklist_daisies', label: t('subscription.preferences.blacklistDaisies', 'Daisies'), category: 'types' },
  { id: 'blacklist_orchids', label: t('subscription.preferences.blacklistOrchids', 'Orchids'), category: 'types' },
  { id: 'blacklist_peonies', label: t('subscription.preferences.blacklistPeonies', 'Peonies'), category: 'types' },
  { id: 'blacklist_hydrangeas', label: t('subscription.preferences.blacklistHydrangeas', 'Hydrangeas'), category: 'types' },
  { id: 'blacklist_red_flowers', label: t('subscription.preferences.blacklistRedFlowers', 'Red flowers'), category: 'colors' },
  { id: 'blacklist_white_flowers', label: t('subscription.preferences.blacklistWhiteFlowers', 'White flowers'), category: 'colors' },
  { id: 'blacklist_yellow_flowers', label: t('subscription.preferences.blacklistYellowFlowers', 'Yellow flowers'), category: 'colors' },
  { id: 'blacklist_pink_flowers', label: t('subscription.preferences.blacklistPinkFlowers', 'Pink flowers'), category: 'colors' },
  { id: 'blacklist_purple_flowers', label: t('subscription.preferences.blacklistPurpleFlowers', 'Purple flowers'), category: 'colors' },
  { id: 'blacklist_scented_flowers', label: t('subscription.preferences.blacklistScentedFlowers', 'Scented flowers'), category: 'properties' },
  { id: 'blacklist_non_scented_flowers', label: t('subscription.preferences.blacklistNonScentedFlowers', 'Non-scented flowers'), category: 'properties' },
];

// Helper function to get optimized fruit image assets
const getFruitImageAssets = (fruitKey: keyof typeof imageAssets) => {
  return imageAssets[fruitKey];
};

export const getFruitBlacklistItems = (t: any): BlacklistItem[] => [
  { id: 'blacklist_mango', label: t('subscription.fruitPreferences.blacklistMango', 'Mango'), category: 'specific', image: getFruitImageAssets('blacklistMango') },
  { id: 'blacklist_papaya', label: t('subscription.fruitPreferences.blacklistPapaya', 'Papaya'), category: 'specific', image: getFruitImageAssets('blacklistPapaya') },
  { id: 'blacklist_dragon_fruit', label: t('subscription.fruitPreferences.blacklistDragonFruit', 'Dragon fruit'), category: 'specific', image: getFruitImageAssets('blacklistDragonfruit') },
  { id: 'blacklist_rambutan', label: t('subscription.fruitPreferences.blacklistRambutan', 'Rambutan'), category: 'specific', image: getFruitImageAssets('blacklistRambutan') },
  { id: 'blacklist_lychee', label: t('subscription.fruitPreferences.blacklistLychee', 'Lychee'), category: 'specific', image: getFruitImageAssets('blacklistLychee') },
  { id: 'blacklist_longan', label: t('subscription.fruitPreferences.blacklistLongan', 'Longan'), category: 'specific', image: getFruitImageAssets('blacklistLongan') },
  { id: 'blacklist_durian', label: t('subscription.fruitPreferences.blacklistDurian', 'Durian'), category: 'specific', image: getFruitImageAssets('blacklistDurian') },
  { id: 'blacklist_mangosteen', label: t('subscription.fruitPreferences.blacklistMangosteen', 'Mangosteen'), category: 'specific', image: getFruitImageAssets('blacklistMangosteen') },
  { id: 'blacklist_jackfruit', label: t('subscription.fruitPreferences.blacklistJackfruit', 'Jackfruit'), category: 'specific', image: getFruitImageAssets('blacklistJackfruit') },
  { id: 'blacklist_starfruit', label: t('subscription.fruitPreferences.blacklistStarfruit', 'Starfruit'), category: 'specific', image: getFruitImageAssets('blacklistStarfruit') },
  { id: 'blacklist_guava', label: t('subscription.fruitPreferences.blacklistGuava', 'Guava'), category: 'specific', image: getFruitImageAssets('blacklistGuava') },
  { id: 'blacklist_passion_fruit', label: t('subscription.fruitPreferences.blacklistPassionFruit', 'Passion fruit'), category: 'specific', image: getFruitImageAssets('blacklistPassionfruit') },
  { id: 'blacklist_rose_apple', label: t('subscription.fruitPreferences.blacklistRoseApple', 'Rose apple'), category: 'specific', image: getFruitImageAssets('blacklistRoseapple') },
  { id: 'blacklist_tamarind', label: t('subscription.fruitPreferences.blacklistTamarind', 'Tamarind'), category: 'specific', image: getFruitImageAssets('blacklistTamarind') },
  { id: 'blacklist_snake_fruit', label: t('subscription.fruitPreferences.blacklistSnakeFruit', 'Snake fruit (Salak)'), category: 'specific', image: getFruitImageAssets('blacklistSnakefruit') },
  { id: 'blacklist_coconut', label: t('subscription.fruitPreferences.blacklistCoconut', 'Coconut'), category: 'specific', image: getFruitImageAssets('blacklistCoconut') },
];

const BlacklistModal: React.FC<BlacklistModalProps> = ({
  isOpen,
  onClose,
  selectedItems,
  onSelectionChange,
  serviceType = 'flower'
}) => {
  const { t } = useTranslation();
  const [localSelection, setLocalSelection] = useState<string[]>(selectedItems);

  useEffect(() => {
    setLocalSelection(selectedItems);
  }, [selectedItems]);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      // Store the original overflow value
      const originalOverflow = document.body.style.overflow;
      // Prevent scrolling
      document.body.style.overflow = 'hidden';
      
      // Cleanup function to restore original overflow
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  const blacklistItems = serviceType === 'fruit' ? getFruitBlacklistItems(t) : getFlowerBlacklistItems(t);

  // Group items by category
  const groupedItems = blacklistItems.reduce((groups, item) => {
    const category = item.category || 'other';
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(item);
    return groups;
  }, {} as Record<string, BlacklistItem[]>);

  const handleItemToggle = (itemId: string) => {
    const newSelection = localSelection.includes(itemId)
      ? localSelection.filter(id => id !== itemId)
      : [...localSelection, itemId];
    setLocalSelection(newSelection);
  };

  const handleSave = () => {
    onSelectionChange(localSelection);
    onClose();
  };

  const handleCancel = () => {
    setLocalSelection(selectedItems);
    onClose();
  };

  const getCategoryTitle = (category: string): string => {
    if (serviceType === 'fruit') {
      switch (category) {
        case 'specific':
          return t('subscription.fruitPreferences.categorySpecific', 'Thai & Asian Tropical Fruits');
        default:
          return t('subscription.fruitPreferences.categoryOther', 'Other');
      }
    } else {
      switch (category) {
        case 'types':
          return t('subscription.preferences.categoryTypes', 'Flower Types');
        case 'colors':
          return t('subscription.preferences.categoryColors', 'Colors');
        case 'properties':
          return t('subscription.preferences.categoryProperties', 'Properties');
        default:
          return t('subscription.preferences.categoryOther', 'Other');
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black bg-opacity-50 flex items-center justify-center">
    <div className="fixed inset-0 z-[200] bg-white flex flex-col">
      {/* Header */}
      <div className="bg-white p-4 flex items-center justify-between border-b">
        <button
          onClick={handleCancel}
          className="p-0 bg-transparent border-none"
        >
          <X size={24} />
        </button>
        <h1 className="text-lg font-medium text-gray-900">
          {serviceType === 'fruit' 
            ? t('subscription.fruitPreferences.blacklistTitle', 'Never Include These Fruits')
            : t('subscription.preferences.blacklistTitle', 'Never Include These Flowers')}
        </h1>
        <div className="w-6"></div>
      </div>

      {/* Instructions */}
      <div className="p-5 bg-white">
        <p className="text-base text-black">
          {serviceType === 'fruit'
            ? t('subscription.fruitPreferences.blacklistInstructions', 'Select fruits you absolutely never want to receive. These will be completely excluded from all deliveries.')
            : t('subscription.preferences.blacklistInstructions', 'Select flowers you absolutely never want to receive. These will be completely excluded from all deliveries.')
          }
        </p>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto px-5 space-y-6 pb-10">
        {Object.entries(groupedItems).map(([category, items]) => (
          <div key={category} className="space-y-3">
            <h3 className="text-sm font-medium text-gray-700 border-b border-gray-200 pb-2">
              {getCategoryTitle(category)}
            </h3>
            
            <div className="grid grid-cols-3 gap-3">
              {items.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleItemToggle(item.id)}
                  className={`relative p-2 rounded-xl text-sm font-medium transition-all duration-200 border min-h-[80px] flex flex-col items-center justify-center gap-2 text-center ${
                    localSelection.includes(item.id)
                      ? 'bg-red-100 border-red-400 text-red-800'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
                  }`}
                >
                  {item.image && (
                    <OptimizedImage 
                      src={typeof item.image === 'string' ? item.image : item.image.png}
                      srcWebp={typeof item.image === 'string' ? undefined : item.image.webp}
                      srcAvif={typeof item.image === 'string' ? undefined : item.image.avif}
                      alt={item.label}
                      className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                      loading="lazy"
                      width={48}
                      height={48}
                    />
                  )}
                  <span className="text-xs leading-tight">{item.label}</span>
                  {localSelection.includes(item.id) && (
                    <X size={16} className="flex-shrink-0 absolute top-2 right-2" />
                  )}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      
      {/* Bottom Save Button */}
      <div className="p-4 border-t bg-white">
        <button 
          onClick={handleSave}
          className="w-full bg-amber-400 border-none text-black font-semibold py-4 rounded-full hover:bg-yellow-700 transition-colors"
        >
          {serviceType === 'fruit'
            ? t('subscription.fruitPreferences.saveToBlacklist', 'Save to blacklist')
            : t('subscription.preferences.saveToBlacklist', 'Save to blacklist')
          } ({localSelection.length})
        </button>
      </div>
    </div>
    </div>

  );
};

export default BlacklistModal; 